import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm, useFieldArray } from 'react-hook-form';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiPlus, FiTrash2, FiSave, FiLoader } = FiIcons;

const ClientModal = ({ isOpen, onClose, client = null, onSubmit }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { register, handleSubmit, formState: { errors }, reset, control, watch } = useForm({
    defaultValues: {
      name: '',
      business_name: '',
      email: '',
      phone: '',
      address: '',
      billing_contact: {
        name: '',
        email: '',
        phone: ''
      },
      gstin: '',
      gst_type: 'B2B',
      gst_rate: 18,
      contract_details: {
        start_date: '',
        end_date: '',
        renewal_terms: ''
      },
      service_history: [
        { service: '', start_date: '', status: 'Active' }
      ],
      payment_terms: {
        due_days: 30,
        early_payment_discount: 0,
        late_payment_penalty: 0
      },
      segmentation: {
        service_type: '',
        billing_frequency: 'Monthly',
        account_manager: '',
        payment_status: 'Active'
      },
      status: 'Active',
      projects: 0,
      total_billed: 0,
      monthly_revenue: 0
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "service_history"
  });

  const gstType = watch('gst_type');

  useEffect(() => {
    if (client) {
      reset({
        name: client.name || '',
        business_name: client.business_name || '',
        email: client.email || '',
        phone: client.phone || '',
        address: client.address || '',
        billing_contact: {
          name: client.billing_contact?.name || '',
          email: client.billing_contact?.email || '',
          phone: client.billing_contact?.phone || ''
        },
        gstin: client.gstin || '',
        gst_type: client.gst_type || 'B2B',
        gst_rate: client.gst_rate || 18,
        contract_details: {
          start_date: client.contract_details?.start_date || '',
          end_date: client.contract_details?.end_date || '',
          renewal_terms: client.contract_details?.renewal_terms || ''
        },
        service_history: client.service_history || [{ service: '', start_date: '', status: 'Active' }],
        payment_terms: {
          due_days: client.payment_terms?.due_days || 30,
          early_payment_discount: client.payment_terms?.early_payment_discount || 0,
          late_payment_penalty: client.payment_terms?.late_payment_penalty || 0
        },
        segmentation: {
          service_type: client.segmentation?.service_type || '',
          billing_frequency: client.segmentation?.billing_frequency || 'Monthly',
          account_manager: client.segmentation?.account_manager || '',
          payment_status: client.segmentation?.payment_status || 'Active'
        },
        status: client.status || 'Active',
        projects: client.projects || 0,
        total_billed: client.total_billed || 0,
        monthly_revenue: client.monthly_revenue || 0
      });
    } else {
      reset({
        name: '',
        business_name: '',
        email: '',
        phone: '',
        address: '',
        billing_contact: {
          name: '',
          email: '',
          phone: ''
        },
        gstin: '',
        gst_type: 'B2B',
        gst_rate: 18,
        contract_details: {
          start_date: '',
          end_date: '',
          renewal_terms: ''
        },
        service_history: [
          { service: '', start_date: '', status: 'Active' }
        ],
        payment_terms: {
          due_days: 30,
          early_payment_discount: 0,
          late_payment_penalty: 0
        },
        segmentation: {
          service_type: '',
          billing_frequency: 'Monthly',
          account_manager: '',
          payment_status: 'Active'
        },
        status: 'Active',
        projects: 0,
        total_billed: 0,
        monthly_revenue: 0
      });
    }
  }, [client, reset]);

  const handleFormSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await onSubmit(data);
      onClose();
    } catch (error) {
      console.error('Error submitting client:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const serviceOptions = [
    'SEO', 'PPC', 'Social Media Marketing', 'Web Development', 
    'Content Marketing', 'Email Marketing', 'Brand Management', 
    'Analytics & Reporting', 'Conversion Optimization'
  ];

  const accountManagers = [
    'Alice Cooper', 'Bob Wilson', 'Carol White', 'David Brown', 
    'Emily Davis', 'Frank Miller', 'Grace Taylor', 'Henry Clark'
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          
          <motion.div
            className="relative bg-white rounded-xl w-full max-w-5xl max-h-[95vh] overflow-y-auto shadow-xl"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-800">
                    {client ? 'Edit Client Profile' : 'Create New Client'}
                  </h2>
                  <p className="text-sm text-gray-600">
                    {client ? 'Update client information and settings' : 'Add comprehensive client details'}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  disabled={isSubmitting}
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit(handleFormSubmit)} className="p-6 space-y-8">
              {/* Basic Information */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiIcons.FiUser} className="w-5 h-5 mr-2" />
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client Name *
                    </label>
                    <input
                      type="text"
                      {...register('name', { required: 'Client name is required' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter client full name"
                    />
                    {errors.name && (
                      <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Business Name *
                    </label>
                    <input
                      type="text"
                      {...register('business_name', { required: 'Business name is required' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter business/company name"
                    />
                    {errors.business_name && (
                      <p className="text-red-500 text-sm mt-1">{errors.business_name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      {...register('email', { 
                        required: 'Email is required',
                        pattern: {
                          value: /^\S+@\S+$/i,
                          message: 'Invalid email address'
                        }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter email address"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      {...register('phone', { required: 'Phone number is required' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter phone number with country code"
                    />
                    {errors.phone && (
                      <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Business Address
                    </label>
                    <textarea
                      {...register('address')}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter complete business address"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client Status
                    </label>
                    <select
                      {...register('status')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                      <option value="Pending">Pending</option>
                      <option value="Suspended">Suspended</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Billing Contact Information */}
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiIcons.FiCreditCard} className="w-5 h-5 mr-2" />
                  Billing Contact Information
                </h3>
                <p className="text-sm text-gray-600 mb-4">Separate billing contact (leave empty to use primary contact)</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Billing Contact Name
                    </label>
                    <input
                      type="text"
                      {...register('billing_contact.name')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter billing contact name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Billing Contact Email
                    </label>
                    <input
                      type="email"
                      {...register('billing_contact.email')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter billing email"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Billing Contact Phone
                    </label>
                    <input
                      type="tel"
                      {...register('billing_contact.phone')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter billing phone"
                    />
                  </div>
                </div>
              </div>

              {/* GST Information */}
              <div className="bg-green-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiIcons.FiFileText} className="w-5 h-5 mr-2" />
                  GST Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      GST Type *
                    </label>
                    <select
                      {...register('gst_type', { required: 'GST type is required' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="B2B">B2B (Business to Business)</option>
                      <option value="B2C">B2C (Business to Consumer)</option>
                    </select>
                    {errors.gst_type && (
                      <p className="text-red-500 text-sm mt-1">{errors.gst_type.message}</p>
                    )}
                  </div>

                  {gstType === 'B2B' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        GSTIN (GST Identification Number)
                      </label>
                      <input
                        type="text"
                        {...register('gstin', {
                          pattern: {
                            value: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
                            message: 'Invalid GSTIN format'
                          }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="27AABCT1332L000"
                      />
                      {errors.gstin && (
                        <p className="text-red-500 text-sm mt-1">{errors.gstin.message}</p>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      GST Rate (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      {...register('gst_rate', { 
                        required: 'GST rate is required',
                        min: { value: 0, message: 'GST rate cannot be negative' },
                        max: { value: 100, message: 'GST rate cannot exceed 100%' }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="18"
                    />
                    {errors.gst_rate && (
                      <p className="text-red-500 text-sm mt-1">{errors.gst_rate.message}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Contract Details */}
              <div className="bg-purple-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiIcons.FiCalendar} className="w-5 h-5 mr-2" />
                  Contract Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contract Start Date
                    </label>
                    <input
                      type="date"
                      {...register('contract_details.start_date')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contract End Date
                    </label>
                    <input
                      type="date"
                      {...register('contract_details.end_date')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Renewal Terms
                    </label>
                    <textarea
                      {...register('contract_details.renewal_terms')}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Auto-renewal with 30 days notice, Manual renewal required, etc."
                    />
                  </div>
                </div>
              </div>

              {/* Service History */}
              <div className="bg-orange-50 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                    <SafeIcon icon={FiIcons.FiSettings} className="w-5 h-5 mr-2" />
                    Service History
                  </h3>
                  <button
                    type="button"
                    onClick={() => append({ service: '', start_date: '', status: 'Active' })}
                    className="flex items-center space-x-2 px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                  >
                    <SafeIcon icon={FiPlus} className="w-4 h-4" />
                    <span>Add Service</span>
                  </button>
                </div>
                <div className="space-y-3">
                  {fields.map((field, index) => (
                    <div key={field.id} className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 border border-gray-200 rounded-lg bg-white">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Service Type
                        </label>
                        <select
                          {...register(`service_history.${index}.service`)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="">Select service</option>
                          {serviceOptions.map(service => (
                            <option key={service} value={service}>{service}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Start Date
                        </label>
                        <input
                          type="date"
                          {...register(`service_history.${index}.start_date`)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Status
                        </label>
                        <select
                          {...register(`service_history.${index}.status`)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="Active">Active</option>
                          <option value="Paused">Paused</option>
                          <option value="Completed">Completed</option>
                          <option value="Cancelled">Cancelled</option>
                        </select>
                      </div>

                      <div className="flex items-end">
                        <button
                          type="button"
                          onClick={() => remove(index)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                          disabled={fields.length === 1}
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Payment Terms */}
              <div className="bg-red-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiIcons.FiDollarSign} className="w-5 h-5 mr-2" />
                  Payment Terms
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Due (Days) *
                    </label>
                    <input
                      type="number"
                      {...register('payment_terms.due_days', { 
                        required: 'Payment due days is required',
                        min: { value: 1, message: 'Must be at least 1 day' }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="30"
                    />
                    {errors.payment_terms?.due_days && (
                      <p className="text-red-500 text-sm mt-1">{errors.payment_terms.due_days.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Early Payment Discount (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      {...register('payment_terms.early_payment_discount', {
                        min: { value: 0, message: 'Cannot be negative' },
                        max: { value: 100, message: 'Cannot exceed 100%' }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Late Payment Penalty (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      {...register('payment_terms.late_payment_penalty', {
                        min: { value: 0, message: 'Cannot be negative' }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

              {/* Client Segmentation */}
              <div className="bg-indigo-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiIcons.FiTarget} className="w-5 h-5 mr-2" />
                  Client Segmentation
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Primary Service Type
                    </label>
                    <select
                      {...register('segmentation.service_type')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select primary service</option>
                      {serviceOptions.map(service => (
                        <option key={service} value={service}>{service}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Billing Frequency
                    </label>
                    <select
                      {...register('segmentation.billing_frequency')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="Monthly">Monthly</option>
                      <option value="Quarterly">Quarterly</option>
                      <option value="Semi-Annual">Semi-Annual</option>
                      <option value="Annual">Annual</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account Manager
                    </label>
                    <select
                      {...register('segmentation.account_manager')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select account manager</option>
                      {accountManagers.map(manager => (
                        <option key={manager} value={manager}>{manager}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Payment Status
                    </label>
                    <select
                      {...register('segmentation.payment_status')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="Active">Active</option>
                      <option value="Pending">Pending</option>
                      <option value="Overdue">Overdue</option>
                      <option value="Suspended">Suspended</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Financial Information */}
              <div className="bg-yellow-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiIcons.FiTrendingUp} className="w-5 h-5 mr-2" />
                  Financial Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Active Projects
                    </label>
                    <input
                      type="number"
                      {...register('projects', { min: { value: 0, message: 'Cannot be negative' } })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Total Billed ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      {...register('total_billed', { min: { value: 0, message: 'Cannot be negative' } })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Monthly Revenue ($)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      {...register('monthly_revenue', { min: { value: 0, message: 'Cannot be negative' } })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex space-x-3 pt-6 border-t border-gray-200 sticky bottom-0 bg-white">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 font-medium"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 font-medium flex items-center justify-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiSave} className="w-4 h-4" />
                      <span>{client ? 'Update Client' : 'Create Client'}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ClientModal;